import { useEffect, useRef } from 'react'

// Silently re-fetches data on a periodic interval and when the browser tab
// regains focus after being hidden. The refetch callback should be a "silent"
// fetch — it updates state without flashing a loading spinner.
//
// Usage:
//   useAutoRefresh(fetchSilent, { intervalMs: 90_000 })
//
// Options:
//   intervalMs   – polling interval while the tab is visible (default 90s)
//   enabled      – set false to pause (e.g. while a modal is open)
//   onFocus      – also refetch when user returns to the tab (default true)

export function useAutoRefresh(refetchFn, {
  intervalMs = 90_000,
  enabled = true,
  onFocus = true,
} = {}) {
  const refetchRef = useRef(refetchFn)
  refetchRef.current = refetchFn

  useEffect(() => {
    if (!enabled) return

    // ── Periodic background poll ──────────────────────────────────────
    let timer = null
    const startTimer = () => {
      timer = setInterval(() => {
        if (!document.hidden) {
          refetchRef.current()
        }
      }, intervalMs)
    }
    startTimer()

    // ── Visibility change: refetch when user comes back ───────────────
    const handleVisibility = () => {
      if (!document.hidden && onFocus) {
        refetchRef.current()
        // Reset interval so it's a full period from now
        clearInterval(timer)
        startTimer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // ── Window focus (covers switching between desktop apps) ──────────
    const handleFocus = () => {
      if (onFocus) {
        refetchRef.current()
        clearInterval(timer)
        startTimer()
      }
    }
    window.addEventListener('focus', handleFocus)

    // ── Connection recovered: the watchdog detected a wedged Supabase client
    // and un-wedged it. Refetch immediately — the focus/interval refetches
    // that fired while it was wedged all hung or failed, so views may be
    // showing stale or empty data right now.
    const handleRecovered = () => {
      refetchRef.current()
      clearInterval(timer)
      startTimer()
    }
    window.addEventListener('chatrone:connection-recovered', handleRecovered)

    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('chatrone:connection-recovered', handleRecovered)
    }
  }, [intervalMs, enabled, onFocus])
}
